import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useQuery, useMutation } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import Styled from 'styled';

import { RouteWithSearchRenderProps as RouteWithSearchProps } from 'lib/react-router/route-with-search';
import ImageView from 'lib/images/image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import ErrorPage from 'four-nets/errors/error-page';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import query, { AlbumQuery, AlbumQueryVariables } from 'four-nets/photoblog/posts/album/album-query.graphql';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { AlertContext } from 'lib/alert-context/alert-context';
import deleteIcon from 'four-nets/icons/svg/icon_delete.svg';
import closeIcon from 'four-nets/icons/svg/icon_close.svg';

import ImagesEditHeader from '../images-edit-header/images-edit-header';
import useWriteImagesCache from '../../use-write-images-cache/use-write-images-cache';
import { FIRST } from '../../detail/detail-loader';

import mutation, { UpdateAlbumImagesMutation, UpdateAlbumImagesMutationVariables } from './images-edit.graphql';
import defaultStyles from './images-edit.scss';

interface Params {
    slug: string;
    username: string;
}

export interface Props extends RouteWithSearchProps<Params> {
    styles: typeof defaultStyles;
    excludeHeader?: boolean;
    onClose?: () => void;
}

const Edit: React.FC<Props> = props => {
    const { viewer } = useContext(ViewerContext);
    const { fail } = useContext(AlertContext);

    const history = useHistory();
    const writeImagesCache = useWriteImagesCache();

    const {
        styles,
        match: {
            params: { username, slug },
        },
        excludeHeader,
        onClose,
    } = props;

    const [titles, setTitles] = useState(new Map());
    const [removal, setRemoval] = useState(new Map());
    const [dirty, setDirty] = useState(false);

    const { data, error, loading } = useQuery<AlbumQuery, AlbumQueryVariables>(query, {
        variables: { slug, username },
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    });
    const [updateAlbumImages, { loading: submitting }] = useMutation<
        UpdateAlbumImagesMutation,
        UpdateAlbumImagesMutationVariables
    >(mutation);

    useEffect(() => {
        const defaulTitles = new Map();
        if (data && data.postBySlug && data.postBySlug.images && data.postBySlug.images.edges) {
            data.postBySlug.images.edges.forEach(edge => {
                if (edge && edge.node) {
                    defaulTitles.set(edge.node.id, edge.node.title || '');
                }
            });
        }
        setTitles(defaulTitles);
    }, [data]);

    if (!viewer || username !== viewer.username) {
        return <Unauthorized />;
    }

    const onSubmit = () => {
        if (data && data.postBySlug && data.postBySlug.images && data.postBySlug.images.edges) {
            const images = data.postBySlug.images.edges
                .map(edge => ({
                    id: edge!.node!.id,
                    title: titles.get(edge!.node!.id),
                }))
                .filter(e => !removal.has(e.id));

            return updateAlbumImages({ variables: { input: { id: data.postBySlug.id, images } } }).then(result => {
                if (result.data?.updateAlbumImages?.post?.images?.edges.length) {
                    writeImagesCache(
                        result.data.updateAlbumImages.post.id,
                        result.data.updateAlbumImages.post.images.edges
                    );
                }
            });
        }
        return Promise.resolve() as any;
    };

    return (
        <>
            {excludeHeader ? null : (
                <ImagesEditHeader
                    disabled={loading || submitting || !dirty}
                    dirty={dirty}
                    onSubmit={() => {
                        onSubmit()
                            .then(() => {
                                if (onClose) {
                                    onClose();
                                }

                                history.push({
                                    pathname: data! && data!.postBySlug! && data!.postBySlug!.href,
                                    state: { ommitFirst: true },
                                });
                            })
                            .catch((err: Error) => {
                                fail!({
                                    body: err.message,
                                    onAccept: () => {
                                        /* pass */
                                    },
                                });
                            });
                    }}
                    {...(data! && data!.postBySlug!)}
                />
            )}
            <div className={styles.images}>
                <LoadingWrapperView loading={loading || submitting}>
                    <ErrorPage error={error}>
                        {data &&
                            data.postBySlug &&
                            data.postBySlug.images &&
                            data.postBySlug.images.edges!.map(edge => (
                                <div className={styles.tile}>
                                    <button
                                        className={styles.button}
                                        onClick={() => {
                                            if (!dirty) {
                                                setDirty(true);
                                            }

                                            removal.set(edge!.node!.id, !Boolean(removal.get(edge!.node!.id)));
                                            setRemoval(new Map(removal));
                                        }}
                                        type='button'
                                    >
                                        <SVGIcon
                                            style={{ image: styles.icon }}
                                            src={removal.get(edge!.node!.id) ? closeIcon : deleteIcon}
                                        />
                                    </button>
                                    <div
                                        className={classnames(styles.image, {
                                            [styles.selectedForRemoval]: removal.get(edge!.node!.id),
                                        })}
                                    >
                                        <ImageView
                                            aspectFill={true}
                                            sizes={{ xs: 250 }}
                                            srcSet={
                                                (edge!.node && edge!.node.photoFile && edge!.node.photoFile.srcset) ||
                                                ''
                                            }
                                            src={(edge!.node && edge!.node.photoFile && edge!.node.photoFile.src) || ''}
                                        />
                                    </div>
                                    <FormattedMessage id='ADD_IMAGE_DESCRIPTION'>
                                        {msg => (
                                            <textarea
                                                className={styles.description}
                                                placeholder={String(msg)}
                                                disabled={removal.get(edge!.node!.id)}
                                                onChange={event => {
                                                    if (!dirty) {
                                                        setDirty(true);
                                                    }

                                                    if (event.target.value.length < 250) {
                                                        titles.set(edge!.node!.id, event.target.value);
                                                        setTitles(new Map(titles));
                                                    }
                                                }}
                                                value={titles.get(edge!.node!.id)}
                                            />
                                        )}
                                    </FormattedMessage>
                                </div>
                            ))}
                    </ErrorPage>
                </LoadingWrapperView>
            </div>
            {excludeHeader ? (
                <button
                    className={styles.submitButton}
                    onClick={() => {
                        onSubmit().then(onClose);
                    }}
                >
                    <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                </button>
            ) : null}
        </>
    );
};

export default Styled(defaultStyles)(Edit);
